import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft, Home, ArrowRight, FileText, Sparkles } from 'lucide-react';
import { useLocaleContext } from '../context/LocaleContext';
import { SEOHead } from '../components/SEOHead';
import { getAllArticles } from '../knowledge/knowledgeArticles';
import { getKnowledgeArticleUrlSync } from '../knowledge/slugMapping';
import { locales, urlMappings } from '../utils/hreflang';
import { getAllPosts } from '../blog/blogPosts';
import { BlogPreviewCard } from '../blog/BlogPreviewCard';

/**
 * Knowledge Base Landing Page
 *
 * Displays all available knowledge articles with localized links.
 *
 * Localized Routes:
 * - English: /knowledge
 * - German: /de/wissen
 * - Portuguese: /br/conhecimento
 * - Malay: /my/pengetahuan
 */
export const KnowledgeBase: React.FC = () => {
  const { locale } = useLocaleContext();

  // Get all articles for the current locale
  const articles = getAllArticles(locale);

  // Get recommended blog posts (top 4 most relevant to watermarks)
  const allBlogPosts = getAllPosts(locale);
  const recommendedSlugs = [
    'can-turnitin-detect-chatgpt',
    'invisible-chatgpt-watermarks',
    'how-to-check-chatgpt-watermark',
    'why-short-texts-break-detection'
  ];
  const recommendedPosts = recommendedSlugs
    .map(slug => allBlogPosts.find(post => post.slug === slug))
    .filter((post): post is NonNullable<typeof post> => post !== undefined)
    .slice(0, 4);

  // pSEO guides configuration
  const pseoGuides = [
    {
      slug: '/remove-chatgpt-watermarks',
      titleKey: 'chatgptGuideTitle',
      descKey: 'chatgptGuideDesc',
    },
    {
      slug: '/remove-gemini-watermarks',
      titleKey: 'geminiGuideTitle',
      descKey: 'geminiGuideDesc',
    },
    {
      slug: '/remove-claude-watermarks',
      titleKey: 'claudeGuideTitle',
      descKey: 'claudeGuideDesc',
    },
    {
      slug: '/ai-watermark-remover-students',
      titleKey: 'studentGuideTitle',
      descKey: 'studentGuideDesc',
    },
  ];

  // Get localized URL helper
  const getLocalizedUrl = (path: string): string => {
    const prefix = locales[locale] || '';

    // Check if this path has a translation in urlMappings
    const mappings = urlMappings[path];
    if (mappings && mappings[locale]) {
      return `${prefix}${mappings[locale]}`;
    }

    // No translation, use the original path
    return `${prefix}${path}`;
  };

  // Get localized text
  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      title: {
        en: 'Knowledge Hub',
        de: 'Wissenszentrum',
        'pt-BR': 'Centro de Conhecimento',
        'ms-MY': 'Pusat Pengetahuan',
        es: 'Centro de Conocimiento',
        fr: 'Centre de Connaissances',
        ja: 'ナレッジセンター',
        id: 'Pusat Pengetahuan',
      },
      intro: {
        en: 'This knowledge hub provides in-depth technical explanations of AI watermarking concepts, detection methods, and removal techniques. Each article covers fundamental mechanisms, practical applications, and technical limitations.',
        de: 'Dieses Wissenszentrum bietet detaillierte technische Erklärungen zu KI-Wasserzeichenkonzepten, Erkennungsmethoden und Entfernungstechniken. Jeder Artikel behandelt grundlegende Mechanismen, praktische Anwendungen und technische Einschränkungen.',
        'pt-BR': 'Este centro de conhecimento fornece explicações técnicas aprofundadas sobre conceitos de marcas d\'água de IA, métodos de detecção e técnicas de remoção. Cada artigo aborda mecanismos fundamentais, aplicações práticas e limitações técnicas.',
        'ms-MY': 'Pusat pengetahuan ini menyediakan penjelasan teknikal mendalam tentang konsep tanda air AI, kaedah pengesanan, dan teknik penyingkiran. Setiap artikel merangkumi mekanisme asas, aplikasi praktikal, dan had teknikal.',
        es: 'Este centro de conocimiento proporciona explicaciones técnicas detalladas sobre conceptos de marcas de agua de IA, métodos de detección y técnicas de eliminación. Cada artículo cubre mecanismos fundamentales, aplicaciones prácticas y limitaciones técnicas.',
        fr: 'Ce centre de connaissances propose des explications techniques approfondies sur les concepts de tatouage numérique IA, les méthodes de détection et les techniques de suppression. Chaque article couvre les mécanismes fondamentaux, les applications pratiques et les limitations techniques.',
        ja: 'このナレッジセンターでは、AIウォーターマークの概念、検出方法、除去技術について詳細な技術解説を提供しています。各記事では、基本的なメカニズム、実践的な応用、技術的な制限について解説しています。',
        id: 'Pusat pengetahuan ini menyediakan penjelasan teknis mendalam tentang konsep watermark AI, metode deteksi, dan teknik penghapusan. Setiap artikel membahas mekanisme dasar, aplikasi praktis, dan batasan teknis.',
      },
      aboutHeading: {
        en: 'About This Knowledge Base',
        de: 'Über diese Wissensdatenbank',
        'pt-BR': 'Sobre Esta Base de Conhecimento',
        'ms-MY': 'Tentang Pangkalan Pengetahuan Ini',
        es: 'Acerca de Esta Base de Conocimiento',
        fr: 'À propos de cette base de connaissances',
        ja: 'このナレッジベースについて',
        id: 'Tentang Basis Pengetahuan Ini',
      },
      aboutContent: {
        en: 'This collection serves as a technical reference for understanding how invisible Unicode characters end up in AI-generated text, how detection systems actually identify AI writing, and how removal tools clean documents. Articles are written for users who need accurate, non-promotional explanations, not marketing claims.',
        de: 'Diese Sammlung dient als technische Referenz zum Verständnis, wie unsichtbare Unicode-Zeichen in KI-generiertem Text landen, wie Erkennungssysteme KI-Texte tatsächlich identifizieren und wie Entfernungstools Dokumente bereinigen. Artikel sind für Nutzer geschrieben, die präzise, nicht-werbliche Erklärungen benötigen, keine Marketingaussagen.',
        'pt-BR': 'Esta coleção serve como referência técnica para entender como caracteres Unicode invisíveis acabam no texto gerado por IA, como os sistemas de detecção realmente identificam textos de IA, e como as ferramentas de remoção limpam documentos. Os artigos são escritos para usuários que precisam de explicações precisas e não promocionais, não de alegações de marketing.',
        'ms-MY': 'Koleksi ini berfungsi sebagai rujukan teknikal untuk memahami bagaimana aksara Unicode tidak kelihatan tersisip dalam teks yang dijana AI, bagaimana sistem pengesanan sebenarnya mengenal pasti teks AI, dan bagaimana alat penyingkiran membersihkan dokumen. Artikel ditulis untuk pengguna yang memerlukan penjelasan tepat dan bukan promosi, bukan dakwaan pemasaran.',
        es: 'Esta colección sirve como referencia técnica para comprender cómo los grandes modelos de lenguaje integran marcas de agua, cómo los sistemas de detección identifican texto generado por IA y cómo las herramientas de eliminación neutralizan patrones estadísticos. Los artículos están escritos para usuarios que necesitan explicaciones precisas y no promocionales de la tecnología de marcas de agua.',
        fr: 'Cette collection sert de référence technique pour comprendre comment les grands modèles de langage intègrent des filigranes, comment les systèmes de détection identifient le texte généré par IA, et comment les outils de suppression neutralisent les motifs statistiques. Les articles sont rédigés pour les utilisateurs qui recherchent des explications précises et objectives sur la technologie de tatouage numérique.',
        ja: 'このコレクションは、大規模言語モデルがどのようにウォーターマークを埋め込むか、検出システムがAI生成テキストをどのように識別するか、除去ツールが統計的パターンをどのように無効化するかを理解するための技術リファレンスです。各記事は、ウォーターマーク技術について正確で客観的な説明を必要とするユーザー向けに執筆されています。',
        id: 'Koleksi ini berfungsi sebagai referensi teknis untuk memahami bagaimana model bahasa besar menanamkan watermark, bagaimana sistem deteksi mengidentifikasi teks yang dihasilkan AI, dan bagaimana alat penghapusan menetralisir pola statistik. Artikel ditulis untuk pengguna yang membutuhkan penjelasan akurat dan objektif tentang teknologi watermark.',
      },
      articlesHeading: {
        en: 'Knowledge Articles',
        de: 'Wissensartikel',
        'pt-BR': 'Artigos de Conhecimento',
        'ms-MY': 'Artikel Pengetahuan',
        es: 'Artículos de Conocimiento',
        fr: 'Articles de référence',
        ja: 'ナレッジ記事',
        id: 'Artikel Pengetahuan',
      },
      relatedHeading: {
        en: 'Related Resources',
        de: 'Verwandte Ressourcen',
        'pt-BR': 'Recursos Relacionados',
        'ms-MY': 'Sumber Berkaitan',
        es: 'Recursos Relacionados',
        fr: 'Ressources connexes',
        ja: '関連リソース',
        id: 'Sumber Daya Terkait',
      },
      relatedHomepage: {
        en: 'Homepage',
        de: 'Startseite',
        'pt-BR': 'Página Inicial',
        'ms-MY': 'Laman Utama',
        es: 'Página Principal',
        fr: 'Page d\'accueil',
        ja: 'ホームページ',
        id: 'Beranda',
      },
      relatedBlog: {
        en: 'Blog',
        de: 'Blog',
        'pt-BR': 'Blog',
        'ms-MY': 'Blog',
        es: 'Blog',
        fr: 'Blog',
        ja: 'ブログ',
        id: 'Blog',
      },
      relatedChatGPT: {
        en: 'ChatGPT Watermark Removal',
        de: 'ChatGPT Wasserzeichen-Entfernung',
        'pt-BR': 'Remoção de Marca d\'Água ChatGPT',
        'ms-MY': 'Penyingkiran Tanda Air ChatGPT',
        es: 'Eliminación de Marcas de Agua de ChatGPT',
        fr: 'Suppression des filigranes ChatGPT',
        ja: 'ChatGPTウォーターマーク除去',
        id: 'Penghapusan Watermark ChatGPT',
      },
      relatedGemini: {
        en: 'Gemini Watermark Removal',
        de: 'Gemini Wasserzeichen-Entfernung',
        'pt-BR': 'Remoção de Marca d\'Água Gemini',
        'ms-MY': 'Penyingkiran Tanda Air Gemini',
        es: 'Eliminación de Marcas de Agua de Gemini',
        fr: 'Suppression des filigranes Gemini',
        ja: 'Geminiウォーターマーク除去',
        id: 'Penghapusan Watermark Gemini',
      },
      relatedClaude: {
        en: 'Claude Watermark Removal',
        de: 'Claude Wasserzeichen-Entfernung',
        'pt-BR': 'Remoção de Marca d\'Água Claude',
        'ms-MY': 'Penyingkiran Tanda Air Claude',
        es: 'Eliminación de Marcas de Agua de Claude',
        fr: 'Suppression des filigranes Claude',
        ja: 'Claudeウォーターマーク除去',
        id: 'Penghapusan Watermark Claude',
      },
      localizationHeading: {
        en: 'How This Knowledge Base Is Localized',
        de: 'Wie diese Wissensdatenbank lokalisiert ist',
        'pt-BR': 'Como Esta Base de Conhecimento É Localizada',
        'ms-MY': 'Bagaimana Pangkalan Pengetahuan Ini Dilokalkan',
        es: 'Cómo Se Localiza Esta Base de Conocimiento',
        fr: 'Comment cette base de connaissances est localisée',
        ja: 'このナレッジベースのローカライズについて',
        id: 'Bagaimana Basis Pengetahuan Ini Dilokalisasi',
      },
      localizationContent: {
        en: 'All articles are available in English, German, Portuguese (Brazil), Malay, Spanish, French, Japanese, and Indonesian. Each translation maintains technical accuracy while adapting terminology for regional clarity. URLs are fully localized to support multilingual SEO.',
        de: 'Alle Artikel sind auf Englisch, Deutsch, Portugiesisch (Brasilien), Malaysisch, Spanisch, Französisch, Japanisch und Indonesisch verfügbar. Jede Übersetzung bewahrt technische Genauigkeit und passt Terminologie für regionale Klarheit an. URLs sind vollständig lokalisiert, um mehrsprachiges SEO zu unterstützen.',
        'pt-BR': 'Todos os artigos estão disponíveis em inglês, alemão, português (Brasil), malaio, espanhol, francês, japonês e indonésio. Cada tradução mantém precisão técnica enquanto adapta terminologia para clareza regional. URLs são totalmente localizados para suportar SEO multilíngue.',
        'ms-MY': 'Semua artikel tersedia dalam bahasa Inggeris, Jerman, Portugis (Brazil), Melayu, Sepanyol, Perancis, Jepun, dan Indonesia. Setiap terjemahan mengekalkan ketepatan teknikal sambil menyesuaikan terminologi untuk kejelasan serantau. URL dilokalisasikan sepenuhnya untuk menyokong SEO berbilang bahasa.',
        es: 'Todos los artículos están disponibles en inglés, alemán, portugués (Brasil), malayo, español, francés, japonés e indonesio. Cada traducción mantiene precisión técnica mientras adapta la terminología para claridad regional. Las URLs están completamente localizadas para apoyar SEO multilingüe.',
        fr: 'Tous les articles sont disponibles en anglais, allemand, portugais (Brésil), malais, espagnol, français, japonais et indonésien. Chaque traduction préserve la précision technique tout en adaptant la terminologie pour une clarté régionale. Les URL sont entièrement localisées pour optimiser le référencement multilingue.',
        ja: 'すべての記事は、英語、ドイツ語、ポルトガル語（ブラジル）、マレー語、スペイン語、フランス語、日本語、インドネシア語でご利用いただけます。各翻訳は技術的な正確さを維持しながら、地域に合わせた用語を使用しています。URLは多言語SEOをサポートするために完全にローカライズされています。',
        id: 'Semua artikel tersedia dalam bahasa Inggris, Jerman, Portugis (Brasil), Melayu, Spanyol, Prancis, Jepang, dan Indonesia. Setiap terjemahan menjaga keakuratan teknis sambil menyesuaikan terminologi untuk kejelasan regional. URL sepenuhnya dilokalkan untuk mendukung SEO multibahasa.',
      },
      readMore: {
        en: 'Read Article',
        de: 'Artikel lesen',
        'pt-BR': 'Ler Artigo',
        'ms-MY': 'Baca Artikel',
        es: 'Leer Artículo',
        fr: 'Lire l\'article',
        ja: '記事を読む',
        id: 'Baca Artikel',
      },
      metaTitle: {
        en: 'AI Watermark Knowledge Hub - Technical Documentation',
        de: 'KI-Wasserzeichen Wissenszentrum - Technische Dokumentation',
        'pt-BR': 'Centro de Conhecimento de Marca d\'Água IA - Documentação Técnica',
        'ms-MY': 'Pusat Pengetahuan Tanda Air AI - Dokumentasi Teknikal',
        es: 'Centro de Conocimiento de Marcas de Agua de IA - Documentación Técnica',
        fr: 'Centre de connaissances sur les filigranes IA - Documentation technique',
        ja: 'AIウォーターマーク・ナレッジセンター - 技術ドキュメント',
        id: 'Pusat Pengetahuan Watermark AI - Dokumentasi Teknis',
      },
      metaDescription: {
        en: 'Technical knowledge base covering AI watermarking mechanisms, detection methods, and removal techniques. In-depth articles on LLM watermarks and token distribution.',
        de: 'Technische Wissensdatenbank über KI-Wasserzeichen-Mechanismen, Erkennungsmethoden und Entfernungstechniken. Detaillierte Artikel zu LLM-Wasserzeichen und Token-Verteilung.',
        'pt-BR': 'Base de conhecimento técnica cobrindo mecanismos de marcas d\'água de IA, métodos de detecção e técnicas de remoção. Artigos aprofundados sobre marcas d\'água LLM e distribuição de tokens.',
        'ms-MY': 'Pangkalan pengetahuan teknikal meliputi mekanisme tanda air AI, kaedah pengesanan, dan teknik penyingkiran. Artikel mendalam tentang tanda air LLM dan taburan token.',
        es: 'Base de conocimiento técnica que cubre mecanismos de marcas de agua de IA, métodos de detección y técnicas de eliminación. Artículos detallados sobre marcas de agua LLM y distribución de tokens.',
        fr: 'Base de connaissances technique couvrant les mécanismes de tatouage numérique IA, les méthodes de détection et les techniques de suppression. Articles approfondis sur les filigranes LLM et la distribution des tokens.',
        ja: 'AIウォーターマークのメカニズム、検出方法、除去技術を網羅した技術ナレッジベース。LLMウォーターマークとトークン分布に関する詳細な記事を掲載。',
        id: 'Basis pengetahuan teknis yang membahas mekanisme watermark AI, metode deteksi, dan teknik penghapusan. Artikel mendalam tentang watermark LLM dan distribusi token.',
      },
      removalGuidesHeading: {
        en: 'AI Watermark Removal Guides',
        de: 'KI-Wasserzeichen-Entfernungs-Guides',
        'pt-BR': 'Guias de Remoção de Marca d\'Água de IA',
        'ms-MY': 'Panduan Penyingkiran Tanda Air AI',
        es: 'Guías de Eliminación de Marcas de Agua de IA',
        fr: 'Guides de suppression des filigranes IA',
        ja: 'AIウォーターマーク除去ガイド',
        id: 'Panduan Penghapusan Watermark AI',
      },
      removalGuidesIntro: {
        en: 'Specialized guides for removing watermarks from specific AI models. Each guide provides step-by-step instructions and best practices.',
        de: 'Spezialisierte Guides zum Entfernen von Wasserzeichen von bestimmten KI-Modellen. Jeder Guide bietet schrittweise Anleitungen und Best Practices.',
        'pt-BR': 'Guias especializados para remover marcas d\'água de modelos de IA específicos. Cada guia fornece instruções passo a passo e melhores práticas.',
        'ms-MY': 'Panduan khusus untuk menyingkirkan tanda air daripada model AI tertentu. Setiap panduan menyediakan arahan langkah demi langkah dan amalan terbaik.',
        es: 'Guías especializadas para eliminar marcas de agua de modelos de IA específicos. Cada guía proporciona instrucciones paso a paso y mejores prácticas.',
        fr: 'Guides spécialisés pour supprimer les filigranes de modèles IA spécifiques. Chaque guide propose des instructions détaillées et les meilleures pratiques.',
        ja: '特定のAIモデルからウォーターマークを除去するための専門ガイド。各ガイドでは、ステップバイステップの手順とベストプラクティスを紹介しています。',
        id: 'Panduan khusus untuk menghapus watermark dari model AI tertentu. Setiap panduan menyediakan instruksi langkah demi langkah dan praktik terbaik.',
      },
      chatgptGuideTitle: {
        en: 'Remove ChatGPT Watermarks',
        de: 'ChatGPT Wasserzeichen entfernen',
        'pt-BR': 'Remover Marcas d\'Água do ChatGPT',
        'ms-MY': 'Buang Tanda Air ChatGPT',
        es: 'Eliminar Marcas de Agua de ChatGPT',
        fr: 'Supprimer les filigranes ChatGPT',
        ja: 'ChatGPTウォーターマークを除去する',
        id: 'Hapus Watermark ChatGPT',
      },
      chatgptGuideDesc: {
        en: 'Learn how to detect and remove watermarks from ChatGPT-generated text. Comprehensive guide covering detection methods and removal techniques.',
        de: 'Erfahren Sie, wie Sie Wasserzeichen in ChatGPT-generiertem Text erkennen und entfernen. Umfassender Guide zu Erkennungsmethoden und Entfernungstechniken.',
        'pt-BR': 'Aprenda a detectar e remover marcas d\'água de texto gerado pelo ChatGPT. Guia abrangente cobrindo métodos de detecção e técnicas de remoção.',
        'ms-MY': 'Pelajari cara mengesan dan menyingkirkan tanda air daripada teks yang dijana oleh ChatGPT. Panduan komprehensif meliputi kaedah pengesanan dan teknik penyingkiran.',
        es: 'Aprende a detectar y eliminar marcas de agua del texto generado por ChatGPT. Guía completa que cubre métodos de detección y técnicas de eliminación.',
        fr: 'Découvrez comment détecter et supprimer les filigranes des textes générés par ChatGPT. Guide complet couvrant les méthodes de détection et les techniques de suppression.',
        ja: 'ChatGPTで生成されたテキストからウォーターマークを検出・除去する方法を学びましょう。検出方法と除去技術を網羅した包括的なガイドです。',
        id: 'Pelajari cara mendeteksi dan menghapus watermark dari teks yang dihasilkan ChatGPT. Panduan lengkap yang mencakup metode deteksi dan teknik penghapusan.',
      },
      geminiGuideTitle: {
        en: 'Remove Gemini Watermarks',
        de: 'Gemini Wasserzeichen entfernen',
        'pt-BR': 'Remover Marcas d\'Água do Gemini',
        'ms-MY': 'Buang Tanda Air Gemini',
        es: 'Eliminar Marcas de Agua de Gemini',
        fr: 'Supprimer les filigranes Gemini',
        ja: 'Geminiウォーターマークを除去する',
        id: 'Hapus Watermark Gemini',
      },
      geminiGuideDesc: {
        en: 'Complete guide to removing watermarks from Google Gemini text. Understand Gemini\'s watermarking approach and effective removal strategies.',
        de: 'Vollständiger Guide zum Entfernen von Wasserzeichen aus Google Gemini-Text. Verstehen Sie Geminis Wasserzeichen-Ansatz und effektive Entfernungsstrategien.',
        'pt-BR': 'Guia completo para remover marcas d\'água do texto do Google Gemini. Entenda a abordagem de marca d\'água do Gemini e estratégias eficazes de remoção.',
        'ms-MY': 'Panduan lengkap untuk menyingkirkan tanda air daripada teks Google Gemini. Fahami pendekatan tanda air Gemini dan strategi penyingkiran yang berkesan.',
        es: 'Guía completa para eliminar marcas de agua del texto de Google Gemini. Comprende el enfoque de marcado de agua de Gemini y estrategias efectivas de eliminación.',
        fr: 'Guide complet pour supprimer les filigranes des textes Google Gemini. Comprenez l\'approche de tatouage numérique de Gemini et les stratégies de suppression efficaces.',
        ja: 'Google Geminiテキストからウォーターマークを除去するための完全ガイド。Geminiのウォーターマーク手法と効果的な除去戦略を理解しましょう。',
        id: 'Panduan lengkap untuk menghapus watermark dari teks Google Gemini. Pahami pendekatan watermark Gemini dan strategi penghapusan yang efektif.',
      },
      claudeGuideTitle: {
        en: 'Remove Claude Watermarks',
        de: 'Claude Wasserzeichen entfernen',
        'pt-BR': 'Remover Marcas d\'Água do Claude',
        'ms-MY': 'Buang Tanda Air Claude',
        es: 'Eliminar Marcas de Agua de Claude',
        fr: 'Supprimer les filigranes Claude',
        ja: 'Claudeウォーターマークを除去する',
        id: 'Hapus Watermark Claude',
      },
      claudeGuideDesc: {
        en: 'How to clean invisible Unicode characters from Claude output, and why Anthropic\'s embedded watermark is a different thing.',
        de: 'Wie Sie unsichtbare Unicode-Zeichen aus Claude-Ausgaben entfernen und warum Anthropics eingebettetes Wasserzeichen eine andere Sache ist.',
        'pt-BR': 'Como limpar caracteres Unicode invisíveis do texto do Claude, e por que a marca d\'água embutida da Anthropic é uma coisa diferente.',
        'ms-MY': 'Cara membersihkan aksara Unicode tidak kelihatan daripada output Claude, dan sebab tanda air terbenam Anthropic adalah perkara yang berbeza.',
        es: 'Cómo limpiar caracteres Unicode invisibles del texto de Claude, y por qué la marca de agua incorporada de Anthropic es algo diferente.',
        fr: 'Comment nettoyer les caractères Unicode invisibles du texte de Claude, et pourquoi le filigrane intégré d\'Anthropic est une chose différente.',
        ja: 'Claudeの出力から不可視のUnicode文字を除去する方法と、Anthropicの組み込み透かしがそれとは別物である理由。',
        id: 'Cara membersihkan karakter Unicode tak terlihat dari output Claude, dan mengapa watermark bawaan Anthropic adalah hal yang berbeda.',
      },
      studentGuideTitle: {
        en: 'AI Watermark Remover for Students',
        de: 'KI-Wasserzeichen-Entferner für Studenten',
        'pt-BR': 'Removedor de Marca d\'Água de IA para Estudantes',
        'ms-MY': 'Penyingkir Tanda Air AI untuk Pelajar',
        es: 'Eliminador de Marcas de Agua de IA para Estudiantes',
        fr: 'Suppresseur de filigranes IA pour étudiants',
        ja: '学生向けAIウォーターマーク除去ツール',
        id: 'Penghapus Watermark AI untuk Pelajar',
      },
      studentGuideDesc: {
        en: 'Student-focused guide to understanding and managing AI watermarks in academic work. Navigate AI detection tools and maintain academic integrity.',
        de: 'Studentenorientierter Guide zum Verstehen und Verwalten von KI-Wasserzeichen in akademischer Arbeit. Navigieren Sie KI-Erkennungstools und wahren Sie akademische Integrität.',
        'pt-BR': 'Guia focado em estudantes para entender e gerenciar marcas d\'água de IA em trabalhos acadêmicos. Navegue ferramentas de detecção de IA e mantenha a integridade acadêmica.',
        'ms-MY': 'Panduan yang tertumpu kepada pelajar untuk memahami dan menguruskan tanda air AI dalam kerja akademik. Navigasi alat pengesanan AI dan kekalkan integriti akademik.',
        es: 'Guía enfocada en estudiantes para comprender y gestionar marcas de agua de IA en trabajos académicos. Navega herramientas de detección de IA y mantén la integridad académica.',
        fr: 'Guide destiné aux étudiants pour comprendre et gérer les filigranes IA dans les travaux académiques. Maîtrisez les outils de détection IA tout en préservant l\'intégrité académique.',
        ja: '学術論文におけるAIウォーターマークの理解と管理に焦点を当てた学生向けガイド。AI検出ツールを理解し、学術的誠実性を維持しましょう。',
        id: 'Panduan yang berfokus pada pelajar untuk memahami dan mengelola watermark AI dalam karya akademis. Pahami alat deteksi AI dan jaga integritas akademik.',
      },
      recommendedBlogHeading: {
        en: 'Recommended Blog Posts',
        de: 'Empfohlene Blog-Beiträge',
        'pt-BR': 'Posts de Blog Recomendados',
        'ms-MY': 'Artikel Blog yang Disyorkan',
        es: 'Publicaciones de Blog Recomendadas',
        fr: 'Articles de blog recommandés',
        ja: 'おすすめのブログ記事',
        id: 'Artikel Blog yang Direkomendasikan',
      },
      recommendedBlogIntro: {
        en: 'Explore our latest insights and practical guides on AI watermarks, detection tools, and best practices.',
        de: 'Entdecken Sie unsere neuesten Erkenntnisse und praktischen Guides zu KI-Wasserzeichen, Erkennungstools und Best Practices.',
        'pt-BR': 'Explore nossos insights mais recentes e guias práticos sobre marcas d\'água de IA, ferramentas de detecção e melhores práticas.',
        'ms-MY': 'Terokai pandangan terkini kami dan panduan praktikal tentang tanda air AI, alat pengesanan, dan amalan terbaik.',
        es: 'Explora nuestros últimos conocimientos y guías prácticas sobre marcas de agua de IA, herramientas de detección y mejores prácticas.',
        fr: 'Découvrez nos dernières analyses et guides pratiques sur les filigranes IA, les outils de détection et les meilleures pratiques.',
        ja: 'AIウォーターマーク、検出ツール、ベストプラクティスに関する最新の知見と実践ガイドをご覧ください。',
        id: 'Jelajahi wawasan terbaru dan panduan praktis kami tentang watermark AI, alat deteksi, dan praktik terbaik.',
      },
    };
    return translations[key]?.[locale] || translations[key]?.['en'] || '';
  };

  return (
    <>
      <SEOHead
        title={getText('metaTitle')}
        description={getText('metaDescription')}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
        {/* H1: Knowledge Hub */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
            <BookOpen className="w-8 h-8 text-accent-600 dark:text-accent-400" aria-hidden="true" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal-900 dark:text-white">
            {getText('title')}
          </h1>
        </div>

        {/* Short intro paragraph */}
        <div className="mb-12">
          <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed">
            {getText('intro')}
          </p>
        </div>

        {/* Section: About This Knowledge Base */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('aboutHeading')}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
            {getText('aboutContent')}
          </p>
        </section>

        {/* Section: Knowledge Articles */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-6">
            {getText('articlesHeading')}
          </h2>

          <div className="grid gap-6">
            {articles.map((article) => {
              const articleUrl = getKnowledgeArticleUrlSync(article.slug, locale);

              return (
                <article
                  key={article.slug}
                  className="bg-white dark:bg-charcoal-800 rounded-xl p-6 shadow-md border border-neutral-200 dark:border-charcoal-700"
                >
                  <Link
                    to={articleUrl}
                    className="group block"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
                            <BookOpen className="w-5 h-5 text-accent-600 dark:text-accent-400" aria-hidden="true" />
                          </div>
                          <h3 className="text-xl font-bold text-charcoal-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                            {article.title}
                          </h3>
                        </div>
                        <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                          {article.metaDescription}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-accent-600 dark:text-accent-400 group-hover:gap-3 transition-all">
                        <span className="text-sm font-medium whitespace-nowrap">
                          {getText('readMore')}
                        </span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                      </div>
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        </section>

        {/* Section: AI Watermark Removal Guides (pSEO) */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('removalGuidesHeading')}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
            {getText('removalGuidesIntro')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pseoGuides.map((guide) => {
              const guideUrl = getLocalizedUrl(guide.slug);

              return (
                <Link
                  key={guide.slug}
                  to={guideUrl}
                  className="group bg-white dark:bg-charcoal-800 rounded-xl p-6 shadow-md border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-accent-100 dark:bg-accent-900/30 rounded-lg flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-accent-600 dark:text-accent-400" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-charcoal-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors mb-2">
                        {getText(guide.titleKey)}
                      </h3>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-3">
                        {getText(guide.descKey)}
                      </p>
                      <span className="flex items-center gap-2 text-accent-600 dark:text-accent-400 font-medium group-hover:gap-3 transition-all">
                        <span className="text-sm">{getText(guide.titleKey)}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Section: Recommended Blog Posts */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('recommendedBlogHeading')}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
            {getText('recommendedBlogIntro')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {recommendedPosts.map((post) => (
              <BlogPreviewCard key={post.slug} post={post} />
            ))}
          </div>
        </section>

        {/* Section: Related Resources */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('relatedHeading')}
          </h2>
          <nav aria-label="Related resources">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <li>
                <Link
                  to={getLocalizedUrl('/')}
                  className="block p-4 bg-white dark:bg-charcoal-800 rounded-lg border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-accent-600 dark:text-accent-400" aria-hidden="true" />
                    <span className="font-medium text-charcoal-900 dark:text-white">
                      {getText('relatedHomepage')}
                    </span>
                  </div>
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedUrl('/blog')}
                  className="block p-4 bg-white dark:bg-charcoal-800 rounded-lg border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-accent-600 dark:text-accent-400" aria-hidden="true" />
                    <span className="font-medium text-charcoal-900 dark:text-white">
                      {getText('relatedBlog')}
                    </span>
                  </div>
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedUrl('/remove-chatgpt-watermarks')}
                  className="block p-4 bg-white dark:bg-charcoal-800 rounded-lg border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 transition-colors"
                >
                  <span className="font-medium text-charcoal-900 dark:text-white">
                    {getText('relatedChatGPT')}
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedUrl('/remove-gemini-watermarks')}
                  className="block p-4 bg-white dark:bg-charcoal-800 rounded-lg border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 transition-colors"
                >
                  <span className="font-medium text-charcoal-900 dark:text-white">
                    {getText('relatedGemini')}
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedUrl('/remove-claude-watermarks')}
                  className="block p-4 bg-white dark:bg-charcoal-800 rounded-lg border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 transition-colors"
                >
                  <span className="font-medium text-charcoal-900 dark:text-white">
                    {getText('relatedClaude')}
                  </span>
                </Link>
              </li>
            </ul>
          </nav>
        </section>

        {/* Section: How This Knowledge Base Is Localized */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('localizationHeading')}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
            {getText('localizationContent')}
          </p>
        </section>
      </div>
    </>
  );
};

export default KnowledgeBase;
